import { useState } from 'react'
import { Download, Calendar, Filter } from 'lucide-react'
import { Button, Card, CardHeader, CardBody } from '../../components/ui'
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts'

// Mock data
const dailyData = [
  { date: '12/01', impressions: 456789, clicks: 16901, ctr: 3.70, cost: 228390 },
  { date: '12/02', impressions: 478234, clicks: 18234, ctr: 3.81, cost: 246159 },
  { date: '12/03', impressions: 445123, clicks: 15789, ctr: 3.55, cost: 213152 },
  { date: '12/04', impressions: 512345, clicks: 19456, ctr: 3.80, cost: 262741 },
  { date: '12/05', impressions: 489012, clicks: 17890, ctr: 3.66, cost: 241617 },
  { date: '12/06', impressions: 534567, clicks: 20123, ctr: 3.77, cost: 271944 },
  { date: '12/07', impressions: 567890, clicks: 21456, ctr: 3.78, cost: 289633 },
]

const campaignData = [
  { name: '春のゲーム祭り', impressions: 234567, clicks: 8765, cost: 456789 },
  { name: '新作タイトルプロモ', impressions: 189012, clicks: 7234, cost: 367890 },
  { name: 'カフェキャンペーン', impressions: 156789, clicks: 5678, cost: 289012 },
  { name: '年末年始セール', impressions: 134567, clicks: 4890, cost: 234567 },
]

const storeData = [
  { name: '渋谷道玄坂店', impressions: 89012, clicks: 3456, cost: 145678 },
  { name: '新宿西口店', impressions: 78901, clicks: 3012, cost: 128901 },
  { name: '池袋東口店', impressions: 67890, clicks: 2567, cost: 112345 },
  { name: '大阪梅田店', impressions: 56789, clicks: 2123, cost: 98765 },
  { name: '横浜駅前店', impressions: 45678, clicks: 1789, cost: 78901 },
]

export default function ReportPage() {
  const [dateRange, setDateRange] = useState({ start: '2025-12-01', end: '2025-12-07' })
  const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'stores'>('overview')

  const totalStats = dailyData.reduce(
    (acc, day) => ({
      impressions: acc.impressions + day.impressions,
      clicks: acc.clicks + day.clicks,
      cost: acc.cost + day.cost,
    }),
    { impressions: 0, clicks: 0, cost: 0 }
  )

  const avgCtr = ((totalStats.clicks / totalStats.impressions) * 100).toFixed(2)
  const avgCpc = Math.round(totalStats.cost / totalStats.clicks)

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">レポート</h1>
          <p className="text-sm text-gray-500 mt-1">広告配信実績の分析</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />}>
            CSVダウンロード
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-4 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              className="form-input w-40"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            />
            <span className="text-gray-500">〜</span>
            <input
              type="date"
              className="form-input w-40"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            />
          </div>
          <div className="flex gap-3">
            <select className="form-select w-40">
              <option value="all">すべてのキャンペーン</option>
            </select>
            <select className="form-select w-40">
              <option value="all">すべての店舗</option>
            </select>
            <Button leftIcon={<Filter className="w-4 h-4" />}>
              レポート生成
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-sm text-gray-500">インプレッション</p>
            <p className="text-2xl font-bold text-gray-900">{totalStats.impressions.toLocaleString()}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-sm text-gray-500">クリック</p>
            <p className="text-2xl font-bold text-gray-900">{totalStats.clicks.toLocaleString()}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-sm text-gray-500">CTR</p>
            <p className="text-2xl font-bold text-gray-900">{avgCtr}%</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-sm text-gray-500">コスト</p>
            <p className="text-2xl font-bold text-gray-900">¥{totalStats.cost.toLocaleString()}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-sm text-gray-500">CPC</p>
            <p className="text-2xl font-bold text-gray-900">¥{avgCpc}</p>
          </CardBody>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: '概要' },
            { id: 'campaigns', label: 'キャンペーン別' },
            { id: 'stores', label: '店舗別' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h3 className="font-semibold text-gray-900">パフォーマンス推移</h3>
            </CardHeader>
            <CardBody>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="colorImp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                    <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value: number, name: string) => [
                        name === 'impressions' ? value.toLocaleString() : value.toLocaleString(),
                        name === 'impressions' ? 'インプレッション' : 'クリック'
                      ]}
                    />
                    <Legend />
                    <Area 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="impressions" 
                      stroke="#3b82f6" 
                      fillOpacity={1} 
                      fill="url(#colorImp)" 
                      name="インプレッション"
                    />
                    <Area 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="#10b981" 
                      fillOpacity={1} 
                      fill="url(#colorClicks)" 
                      name="クリック"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="font-semibold text-gray-900">詳細データ</h3>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>日付</th>
                    <th className="text-right">インプレッション</th>
                    <th className="text-right">クリック</th>
                    <th className="text-right">CTR</th>
                    <th className="text-right">コスト</th>
                    <th className="text-right">CPC</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dailyData.map((row) => (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td className="text-right">{row.impressions.toLocaleString()}</td>
                      <td className="text-right">{row.clicks.toLocaleString()}</td>
                      <td className="text-right">{row.ctr.toFixed(2)}%</td>
                      <td className="text-right">¥{row.cost.toLocaleString()}</td>
                      <td className="text-right">¥{Math.round(row.cost / row.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h3 className="font-semibold text-gray-900">キャンペーン別パフォーマンス</h3>
            </CardHeader>
            <CardBody>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={campaignData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                    <YAxis type="category" dataKey="name" stroke="#9ca3af" fontSize={11} width={120} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Legend />
                    <Bar dataKey="impressions" fill="#3b82f6" name="インプレッション" />
                    <Bar dataKey="clicks" fill="#10b981" name="クリック" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>キャンペーン</th>
                    <th className="text-right">インプレッション</th>
                    <th className="text-right">クリック</th>
                    <th className="text-right">CTR</th>
                    <th className="text-right">コスト</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {campaignData.map((row) => (
                    <tr key={row.name}>
                      <td className="font-medium">{row.name}</td>
                      <td className="text-right">{row.impressions.toLocaleString()}</td>
                      <td className="text-right">{row.clicks.toLocaleString()}</td>
                      <td className="text-right">{((row.clicks / row.impressions) * 100).toFixed(2)}%</td>
                      <td className="text-right">¥{row.cost.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'stores' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h3 className="font-semibold text-gray-900">店舗別パフォーマンス</h3>
            </CardHeader>
            <CardBody>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={storeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                    <YAxis type="category" dataKey="name" stroke="#9ca3af" fontSize={11} width={100} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Legend />
                    <Bar dataKey="impressions" fill="#3b82f6" name="インプレッション" />
                    <Bar dataKey="clicks" fill="#10b981" name="クリック" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>

          <Card>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>店舗</th>
                    <th className="text-right">インプレッション</th>
                    <th className="text-right">クリック</th>
                    <th className="text-right">CTR</th>
                    <th className="text-right">コスト</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {storeData.map((row) => (
                    <tr key={row.name}>
                      <td className="font-medium">{row.name}</td>
                      <td className="text-right">{row.impressions.toLocaleString()}</td>
                      <td className="text-right">{row.clicks.toLocaleString()}</td>
                      <td className="text-right">{((row.clicks / row.impressions) * 100).toFixed(2)}%</td>
                      <td className="text-right">¥{row.cost.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

